import { useEffect, useRef } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { useHandheld } from './useMediaQuery';

/**
 * The phone's Back button closes the top overlay, not the page under it.
 *
 * On Android (and in an installed PWA, where there is no browser toolbar) Back
 * is how people close things. With the drawer, a sheet, an editor or a dialog
 * open it used to navigate the PAGE: the overlay went with the page, and so
 * did whatever had been typed into it.
 *
 * ## How
 *
 * Opening an overlay pushes one history entry -- a copy of the current one,
 * same URL, same router state, plus a token -- so Back has something to pop
 * that is not the page. A `popstate` that takes that entry away closes the
 * top overlay, and is stopped before React Router hears it, because as far as
 * the router is concerned nothing moved.
 *
 * "Before" is by registration order, not by `capture`: a `popstate` is
 * dispatched AT `window`, and Chromium runs at-target listeners in the order
 * they were added, capture or not. The router adds its listener when it is
 * created, so this module adds its own when it is first evaluated -- which is
 * before `createBrowserRouter` runs, since the router module imports it
 * (through the unsaved-changes guard). Registered lazily, on the first
 * overlay, it ran second: the router saw every overlay pop as a navigation,
 * and that reset its blocker mid-question (#343).
 *
 * Closing an overlay any other way (✕, the scrim, Escape, Save) takes its
 * entry back off with `history.back()`, and that pop is swallowed too, so
 * there is no phantom entry for the next Back to land on -- the "double back"
 * a history entry per overlay is known for.
 *
 * An overlay that closes BECAUSE of a navigation (a folder picked in the
 * drawer) cannot take its entry back: the new page was pushed above it. That
 * entry is now stale, and Back landing on it is skipped straight through to
 * the page below, so it still takes one press to leave the new page.
 *
 * If the router replaced the overlay's entry while it was open (a URL change
 * with `replace`), Back lands on an entry the router did not expect; then
 * the pop is left for the router AND the overlay is closed, which is what
 * Back meant.
 *
 * Handheld only: that is where Back is the way out of an overlay, and where
 * a desktop keyboard already has Escape.
 */

const TOKEN_KEY = '__olsOverlay';

interface Layer {
  token: string;
  close: () => void;
  /** The router location key the overlay was opened over, kept current. */
  routerKey: () => string | undefined;
}

/** Open overlays, bottom first. */
const layers: Layer[] = [];
/** Pops this module caused itself, not yet delivered. */
let ownPops = 0;
/** Pushes waiting for {@link ownPops} to drain: a push racing a back is lost. */
const afterPops: Array<() => void> = [];
let nextToken = 1;
let listening = false;

function tokenOf(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null) return undefined;
  const value = (state as Record<string, unknown>)[TOKEN_KEY];
  return typeof value === 'string' ? value : undefined;
}

function routerKeyOf(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null) return undefined;
  const value = (state as Record<string, unknown>).key;
  return typeof value === 'string' ? value : undefined;
}

function drain(): void {
  while (ownPops === 0 && afterPops.length > 0) afterPops.shift()?.();
}

function onPopState(event: PopStateEvent): void {
  if (ownPops > 0) {
    ownPops -= 1;
    event.stopImmediatePropagation();
    drain();
    return;
  }
  const here = tokenOf(window.history.state);
  const top = layers[layers.length - 1];
  if (top && here !== top.token) {
    // Back, with an overlay up: its entry is the one that went.
    layers.pop();
    if (routerKeyOf(window.history.state) === top.routerKey()) event.stopImmediatePropagation();
    top.close();
    return;
  }
  if (here !== undefined && !layers.some((layer) => layer.token === here)) {
    // A stale overlay entry, left under a page a navigation pushed above it.
    event.stopImmediatePropagation();
    ownPops += 1;
    window.history.back();
  }
}

function listen(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('popstate', onPopState, { capture: true });
}

// Ahead of the router's listener; see the note at the top.
listen();

/** Pushes the layer's entry, now or once this module's own pops have landed. */
function push(layer: Layer): () => void {
  const run = () => {
    const base: unknown = window.history.state;
    const state = typeof base === 'object' && base !== null ? { ...base } : {};
    window.history.pushState({ ...state, [TOKEN_KEY]: layer.token }, '');
    layers.push(layer);
  };
  if (ownPops > 0) afterPops.push(run);
  else run();
  // Cancels a push that has not happened yet.
  return () => {
    const index = afterPops.indexOf(run);
    if (index !== -1) afterPops.splice(index, 1);
  };
}

function release(layer: Layer): void {
  // Never pushed (or already popped by Back): there is nothing to undo.
  const index = layers.indexOf(layer);
  if (index === -1) return;
  layers.splice(index, 1);
  if (tokenOf(window.history.state) === layer.token) {
    ownPops += 1;
    window.history.back();
  }
}

/**
 * While `open`, Back calls `onClose` instead of leaving the page. See above.
 */
export function useBackToClose(open: boolean, onClose: () => void): void {
  const handheld = useHandheld();
  const inRouter = useInRouterContext();
  // Rules of hooks: the router context is fixed for a component's lifetime.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const locationKey = inRouter ? useLocation().key : undefined;
  const closeRef = useRef(onClose);
  const keyRef = useRef(locationKey);
  useEffect(() => {
    closeRef.current = onClose;
    keyRef.current = locationKey;
  });

  const active = open && handheld;
  useEffect(() => {
    if (!active || typeof window === 'undefined') return;
    listen();
    const layer: Layer = {
      token: `o${nextToken++}`,
      close: () => closeRef.current(),
      routerKey: () => keyRef.current,
    };
    const cancelPush = push(layer);
    return () => {
      cancelPush();
      release(layer);
    };
  }, [active]);
}

/**
 * Runs `run` once every `history.back()` this module has issued has landed.
 *
 * For a caller about to move history itself right after an overlay closed:
 * closing takes the overlay's entry back off with `history.back()`, and a
 * second traversal started before that one is delivered races it (the
 * unsaved-changes dialog's Leave is the case: close, then let the router go).
 * Call it after the overlay's cleanup has run, i.e. from an effect.
 */
export function afterOverlayHistorySettles(run: () => void): void {
  if (ownPops > 0) afterPops.push(run);
  else run();
}

/** Test seam: forget every overlay and pending pop. */
export function resetBackToCloseForTests(): void {
  layers.length = 0;
  afterPops.length = 0;
  ownPops = 0;
}
